import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Builds the toast for an error nothing else caught. It names the actual
 * problem, the page it happened on, and a reference that matches the
 * `[Unexpected application error <ref>]` console line, so a support or AI
 * session can find the exact exception and its source file quickly.
 */
export function describeUnexpectedError(error: unknown, page: string, ref: string): string {
  const reason = errorText(error);
  const source = firstAppFrame(error);
  const where = source ? ` · ${source}` : '';
  return `Designflow hit an unexpected problem on ${page}: ${reason}. ` +
    `Reload the page if something looks wrong; contact support if it continues. [ref ${ref}${where}]`;
}

function errorText(error: unknown): string {
  if (typeof error === 'string') return error.slice(0, 200);
  const e = error as { name?: string; message?: unknown } | null;
  const msg = typeof e?.message === 'string' && e.message.trim() ? e.message.trim() : String(error ?? 'unknown error');
  const name = e?.name && e.name !== 'Error' ? `${e.name}: ` : '';
  return (name + msg).slice(0, 200);
}

/** First stack frame from our own code, e.g. "itemLibrary.component.ts:747". */
function firstAppFrame(error: unknown): string | null {
  const stack = (error as { stack?: unknown } | null)?.stack;
  if (typeof stack !== 'string') return null;
  const m = stack.match(/src\/app\/[^\s)]*?([\w.-]+\.ts):(\d+)/) ?? stack.match(/at (\w+\.\w+) /);
  if (!m) return null;
  return m[2] ? `${m[1]}:${m[2]}` : m[1];
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private lastFingerprint = '';
  private lastShownAt = 0;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly zone: NgZone,
  ) {}

  handleError(error: unknown): void {
    const unwrapped = this.unwrap(error);
    const ref = Date.now().toString(36).slice(-6).toUpperCase();
    console.error(`[Unexpected application error ${ref}] page=${location.pathname}`, error);

    // The HTTP interceptor already shows the server's message. Avoid presenting
    // a second, less-specific toast when the same HTTP failure reaches Angular.
    if (unwrapped instanceof HttpErrorResponse) {
      return;
    }

    const fingerprint = this.fingerprint(unwrapped);
    const now = Date.now();
    if (fingerprint === this.lastFingerprint && now - this.lastShownAt < 2000) {
      return;
    }
    this.lastFingerprint = fingerprint;
    this.lastShownAt = now;

    const message = describeUnexpectedError(unwrapped, location.pathname, ref);
    this.zone.run(() => {
      this.snackBar.open(message, 'Close', {
        duration: 10000,
        panelClass: ['error-snackbar'],
      });
    });
  }

  private fingerprint(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }
    return String((error as { message?: unknown } | null)?.message ?? error ?? 'unknown');
  }

  private unwrap(error: unknown): unknown {
    return (error as { rejection?: unknown } | null)?.rejection ?? error;
  }
}
